import { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from '@/components/ui/resizable';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { useOptimizedSupabaseScripts } from '@/hooks/useOptimizedSupabaseScripts';
import { supabase } from '@/integrations/supabase/client';
import jsPDF from 'jspdf';
import { FileText, PhoneOutgoing, PhoneIncoming, Settings, Target, Brain, Zap, Shield, ChevronDown, AlertTriangle, Loader2, HelpCircle, Copy, Calendar, BookOpen, Users, MessageSquare, ChevronRight, Download, Edit3, Save, Info } from 'lucide-react';
import { generateSafeModeScript } from '@/lib/scriptBuilder/safeMode';
import { extractSpokenContent } from '@/lib/scriptBuilder/extractSpokenContent';
import { SafeModeGate, isSafeMode } from './ScriptCreator.SafeModeGate';
import { applyStaticMergeTags } from '@/utils/applyStaticMergeTags';

// Component Props
interface ScriptCreatorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingScript?: any | null;
  preselectedMode?: 'outbound' | 'inbound' | null;
  onScriptCreated?: (script: any) => void;
}

// Form Data Interface
interface ScriptFormData {
  scriptName: string;
  scriptType: 'outbound' | 'inbound';
  scriptMode: 'safe' | 'sales' | 'standard' | '';
  industry: string;
  offer: string;
  callGoal: string;
  tone: string;
  aiProvider: string;
  aiModel: string;
  useLowRiskMode: boolean;
  enableVoicemail: boolean;
  reasonForCall: string;
  targetAudience: string;
  routingOptions: string;
  faqKeywords: string[];
  disqualificationTriggers: string[];
  escalationPrompts: string[];
  appointmentBookingEnabled: boolean;
  appointmentBookingMethod: string;
  appointmentBookingUrl: string;
  appointmentBookingTimezone: string;
  // P.A.I.D. Framework fields
  usePaid: boolean;
  paidPainStarter: string;
  paidAdmissionPrompt: string;
  paidInactionCost: string;
  paidDecisionCTA: string;
  // Fallback Logic fields
  fallbackEnabled: boolean;
  fallbackPrompt: string;
  // User/Company details to eliminate placeholders
  userName: string;
  companyName: string;
  userTitle: string;
  companyPhone: string;
  companyWebsite: string;
  // Safe mode fields
  safeModePurpose?: 'referral' | 'interview' | 'directory' | 'googlemaps' | 'custom';
  area?: string;
  channel?: string;
  service?: string;
  customPurpose?: string;
}

// Configuration Options
const AI_MODELS = {
  openai: [{
    value: 'gpt-4o-mini',
    label: 'GPT-4o Mini (Recommended)'
  }, {
    value: 'gpt-4o',
    label: 'GPT-4o'
  }],
  grok: [{
    value: 'grok-beta',
    label: 'Grok Beta'
  }]
};
const CALL_GOALS = [{
  value: 'generate_leads',
  label: 'Generate Leads'
}, {
  value: 'book_appointments',
  label: 'Book Appointments'
}, {
  value: 'close_sales',
  label: 'Close Sales'
}, {
  value: 'gather_information',
  label: 'Gather Information'
}, {
  value: 'build_relationships',
  label: 'Build Relationships'
}, {
  value: 'follow_up',
  label: 'Follow Up'
}];
const TONE_OPTIONS = [{
  value: 'professional',
  label: 'Professional'
}, {
  value: 'friendly',
  label: 'Friendly'
}, {
  value: 'conversational',
  label: 'Conversational'
}, {
  value: 'authoritative',
  label: 'Authoritative'
}, {
  value: 'consultative',
  label: 'Consultative'
}];
const SAFE_PURPOSE_OPTIONS = [{
  value: 'market_research',
  label: 'Market Research'
}, {
  value: 'industry_survey',
  label: 'Industry Survey'
}, {
  value: 'partnership_inquiry',
  label: 'Partnership Inquiry'
}, {
  value: 'networking',
  label: 'Professional Networking'
}, {
  value: 'information_sharing',
  label: 'Information Sharing'
}, {
  value: 'referral_inquiry',
  label: 'Referral Inquiry/Sourcing'
}, {
  value: 'lead_qualification',
  label: 'Lead Qualification Research'
}, {
  value: 'follow_up_nurturing',
  label: 'Follow-up/Nurturing'
}, {
  value: 'educational_content',
  label: 'Educational Content Sharing'
}, {
  value: 'industry_benchmarking',
  label: 'Industry Benchmarking'
}, {
  value: 'appointment_setting',
  label: 'Appointment Setting (Non-Sales)'
}, {
  value: 'partnership_exploration',
  label: 'Partnership Exploration'
}, {
  value: 'event_invitations',
  label: 'Event/Webinar Invitations'
}, {
  value: 'customer_success',
  label: 'Customer Success Check-ins'
}, {
  value: 'custom',
  label: 'Custom'
}];
const BOOKING_METHODS = [{
  value: 'calendly',
  label: 'Calendly'
}, {
  value: 'acuity',
  label: 'Acuity Scheduling'
}, {
  value: 'booking_page',
  label: 'Custom Booking Page'
}, {
  value: 'phone_callback',
  label: 'Phone Callback'
}];
const TIMEZONE_OPTIONS = [{
  value: 'America/New_York',
  label: 'Eastern Time (ET)'
}, {
  value: 'America/Chicago',
  label: 'Central Time (CT)'
}, {
  value: 'America/Denver',
  label: 'Mountain Time (MT)'
}, {
  value: 'America/Los_Angeles',
  label: 'Pacific Time (PT)'
}];
export function ScriptCreator({
  open,
  onOpenChange,
  editingScript = null,
  preselectedMode = null,
  onScriptCreated
}: ScriptCreatorProps) {
  // State Management
  const [step, setStep] = useState<'select-type' | 'form'>('select-type');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingIntelligence, setIsGeneratingIntelligence] = useState(false);
  const [generatedScript, setGeneratedScript] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedScript, setEditedScript] = useState('');
  const [sectionsOpen, setSectionsOpen] = useState({
    basic: true,
    mode: false,
    userDetails: false,
    ai: false,
    objective: false,
    paid: false,
    fallback: false,
    toggles: false,
    intelligence: false
  });

  // Form Data State
  const [formData, setFormData] = useState<ScriptFormData>({
    scriptName: '',
    scriptType: 'outbound',
    scriptMode: '',
    industry: '',
    offer: '',
    callGoal: '',
    tone: 'professional',
    aiProvider: 'openai',
    aiModel: 'gpt-4o-mini',
    useLowRiskMode: false,
    enableVoicemail: false,
    reasonForCall: '',
    targetAudience: '',
    routingOptions: '',
    faqKeywords: [],
    disqualificationTriggers: [],
    escalationPrompts: [],
    appointmentBookingEnabled: false,
    appointmentBookingMethod: '',
    appointmentBookingUrl: '',
    appointmentBookingTimezone: 'America/New_York',
    // P.A.I.D. Framework fields
    usePaid: false,
    paidPainStarter: '',
    paidAdmissionPrompt: '',
    paidInactionCost: '',
    paidDecisionCTA: '',
    // Fallback Logic fields
    fallbackEnabled: false,
    fallbackPrompt: '🛑 Fallback Response (For Long or Unfocused Calls)\n"Oh — I\'m so sorry. A situation just came up. Would it be okay if I gave you a call back later?"\n(Wait for yes/no. If yes, log consent to call again.)\n\n"Thanks so much. I really appreciate your time today."',
    // User/Company details
    userName: 'Chris',
    companyName: 'Next Level Solutions',
    userTitle: 'Consultant',
    companyPhone: '',
    companyWebsite: ''
  });

  // Hooks
  const {
    toast
  } = useToast();
  const {
    apiKeys: contextApiKeys
  } = useAuth();
  const {
    addScript,
    updateScript
  } = useOptimizedSupabaseScripts();

  // Memoized available AI providers based on user's API keys
  const availableProviders = useMemo(() => [...(contextApiKeys?.openai ? ['openai'] : []), ...(contextApiKeys?.grok ? ['grok'] : [])], [contextApiKeys?.openai, contextApiKeys?.grok]);

  // Auto-populate calendar URLs when appointment booking is enabled
  const handleCalendarPopulation = useCallback(() => {
    if (formData.callGoal === 'book_appointments' && contextApiKeys) {
      if (contextApiKeys.calendlyUrl && !formData.appointmentBookingUrl) {
        setFormData(prev => ({
          ...prev,
          appointmentBookingEnabled: true,
          appointmentBookingMethod: 'calendly',
          appointmentBookingUrl: contextApiKeys.calendlyUrl
        }));
      } else if (contextApiKeys.googleCalendarUrl && !formData.appointmentBookingUrl) {
        setFormData(prev => ({
          ...prev,
          appointmentBookingEnabled: true,
          appointmentBookingMethod: 'booking_page',
          appointmentBookingUrl: contextApiKeys.googleCalendarUrl
        }));
      }
    }
  }, [formData.callGoal, formData.appointmentBookingUrl, contextApiKeys?.calendlyUrl, contextApiKeys?.googleCalendarUrl]);
  useEffect(() => {
    handleCalendarPopulation();
  }, [handleCalendarPopulation]);

  // Initialize form when dialog opens or editing script changes
  useEffect(() => {
    if (open) {
      if (editingScript) {
        // Initialize form with existing script data
        setFormData({
          scriptName: editingScript.name || '',
          scriptType: editingScript.mode || 'outbound',
          scriptMode: editingScript.useLowRiskMode ? 'safe' : 'sales',
          industry: editingScript.targetIndustry || '',
          offer: editingScript.itemSold || '',
          callGoal: '',
          tone: editingScript.tone || 'professional',
          aiProvider: editingScript.aiModel === 'openai' ? 'openai' : 'grok',
          aiModel: editingScript.aiModel || '',
          useLowRiskMode: editingScript.useLowRiskMode || false,
          enableVoicemail: editingScript.enableVoicemail || false,
          reasonForCall: editingScript.reasonForCall || '',
          targetAudience: editingScript.targetAudience || '',
          routingOptions: editingScript.routingOptions || '',
          faqKeywords: editingScript.faqKeywords ? editingScript.faqKeywords.split('\n') : [],
          disqualificationTriggers: editingScript.disqualificationTriggers ? editingScript.disqualificationTriggers.split('\n') : [],
          escalationPrompts: editingScript.escalationPrompts ? editingScript.escalationPrompts.split('\n') : [],
          appointmentBookingEnabled: false,
          appointmentBookingMethod: '',
          appointmentBookingUrl: '',
          appointmentBookingTimezone: 'America/New_York',
          // P.A.I.D. Framework fields
          usePaid: editingScript.use_paid || false,
          paidPainStarter: editingScript.script_sections?.paid?.painStarter || '',
          paidAdmissionPrompt: editingScript.script_sections?.paid?.admissionPrompt || '',
          paidInactionCost: editingScript.script_sections?.paid?.inactionCost || '',
          paidDecisionCTA: editingScript.script_sections?.paid?.decisionCTA || '',
          // Fallback Logic fields
          fallbackEnabled: editingScript.fallback_enabled || false,
          fallbackPrompt: editingScript.fallback_prompt || '🛑 Fallback Response (For Long or Unfocused Calls)\n"Oh — I\'m so sorry. A situation just came up. Would it be okay if I gave you a call back later?"\n(Wait for yes/no. If yes, log consent to call again.)\n\n"Thanks so much. I really appreciate your time today."',
          userName: 'Chris',
          companyName: 'Next Level Solutions',
          userTitle: 'Consultant',
          companyPhone: '',
          companyWebsite: ''
        });
        setGeneratedScript(editingScript.content || '');
        setEditedScript(editingScript.content || '');
        setStep('form');
      } else if (preselectedMode) {
        setFormData(prev => ({
          ...prev,
          scriptType: preselectedMode
        }));
        setStep('form');
      }
    }
  }, [open, editingScript, preselectedMode]);

  // Set default AI provider when available providers change
  const handleDefaultProviderSet = useCallback(() => {
    if (availableProviders.length > 0 && !formData.aiProvider) {
      const defaultProvider = availableProviders[0];
      setFormData(prev => ({
        ...prev,
        aiProvider: defaultProvider,
        aiModel: AI_MODELS[defaultProvider as keyof typeof AI_MODELS]?.[0]?.value || ''
      }));
    }
  }, [availableProviders, formData.aiProvider]);
  useEffect(() => {
    handleDefaultProviderSet();
  }, [handleDefaultProviderSet]);

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      scriptName: '',
      scriptType: 'outbound',
      scriptMode: '',
      industry: '',
      offer: '',
      callGoal: '',
      tone: 'professional',
      aiProvider: 'openai',
      aiModel: 'gpt-4o-mini',
      useLowRiskMode: false,
      enableVoicemail: false,
      reasonForCall: '',
      targetAudience: '',
      routingOptions: '',
      faqKeywords: [],
      disqualificationTriggers: [],
      escalationPrompts: [],
      appointmentBookingEnabled: false,
      appointmentBookingMethod: '',
      appointmentBookingUrl: '',
      appointmentBookingTimezone: 'America/New_York',
      // P.A.I.D. Framework fields
      usePaid: false,
      paidPainStarter: '',
      paidAdmissionPrompt: '',
      paidInactionCost: '',
      paidDecisionCTA: '',
      // Fallback Logic fields
      fallbackEnabled: true,
      fallbackPrompt: '🛑 Fallback Response (For Long or Unfocused Calls)\n"Oh — I\'m so sorry. A situation just came up. Would it be okay if I gave you a call back later?"\n(Wait for yes/no. If yes, log consent to call again.)\n\n"Thanks so much. I really appreciate your time today."',
      // User/Company details - preserve defaults
      userName: 'Chris',
      companyName: 'Next Level Solutions',
      userTitle: 'Consultant',
      companyPhone: '',
      companyWebsite: ''
    });
    setGeneratedScript('');
    setEditedScript('');
    setIsEditing(false);
    setStep('select-type');
    setSectionsOpen({
      basic: true,
      mode: false,
      userDetails: false,
      ai: false,
      objective: false,
      paid: false,
      fallback: false,
      toggles: false,
      intelligence: false
    });
  };

  // AI Intelligence Generation
  const generateIntelligence = async () => {
    if (!formData.industry || !formData.scriptMode) {
      toast({
        title: "Missing required fields",
        description: "Please fill in Industry and Script Mode before generating intelligence.",
        variant: "destructive"
      });
      return;
    }

    // Check for OpenAI API key
    const openaiApiKey = contextApiKeys?.openai;
    if (!openaiApiKey) {
      toast({
        title: "OpenAI API Key Required",
        description: "Please configure your OpenAI API key in API Settings to generate intelligence.",
        variant: "destructive"
      });
      return;
    }
    setIsGeneratingIntelligence(true);
    try {
      const {
        data,
        error
      } = await supabase.functions.invoke('generate-script-intelligence', {
        body: {
          industry: formData.industry,
          offer: formData.offer,
          scriptMode: formData.scriptMode,
          callGoal: formData.callGoal,
          openaiApiKey: openaiApiKey,
          // Pass user details to eliminate placeholders
          userName: formData.userName,
          companyName: formData.companyName,
          userTitle: formData.userTitle,
          companyPhone: formData.companyPhone,
          companyWebsite: formData.companyWebsite,
          // Pass appointment booking settings
          appointmentBookingEnabled: formData.appointmentBookingEnabled,
          appointmentBookingUrl: formData.appointmentBookingUrl,
          appointmentBookingMethod: formData.appointmentBookingMethod
        }
      });
      if (error) {
        console.error('Error generating intelligence:', error);
        toast({
          title: "Error generating intelligence",
          description: error.message || "Failed to generate AI intelligence. Please try again.",
          variant: "destructive"
        });
        return;
      }
      if (data) {
        // Update form data with AI-generated content
        const updates: Partial<ScriptFormData> = {};
        if (data.faqs && Array.isArray(data.faqs)) {
          updates.faqKeywords = data.faqs.map((faq: any) => `Q: ${faq.question}\nA: ${faq.response || faq.answer}`);
        }
        if (data.disqualifiers && Array.isArray(data.disqualifiers)) {
          updates.disqualificationTriggers = data.disqualifiers;
        }
        if (data.escalationPrompts && Array.isArray(data.escalationPrompts)) {
          updates.escalationPrompts = data.escalationPrompts;
        }
        setFormData(prev => ({
          ...prev,
          ...updates
        }));
        toast({
          title: "Intelligence generated",
          description: "AI-powered content has been added to your script sections."
        });
      }
    } catch (error) {
      console.error('Failed to generate intelligence:', error);
      toast({
        title: "Error",
        description: "Failed to generate intelligence. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGeneratingIntelligence(false);
    }
  };

  // Industry-specific content generators to replace placeholders
  const getIndustrySpecificTopic = (industry: string, context: string) => {
    const topics: Record<string, Record<string, string>> = {
      'Roofing': {
        'partnership_opportunities': 'building referral networks with local contractors',
        'business_trends': 'seasonal scheduling and weather-dependent planning'
      },
      'Plumbing': {
        'partnership_opportunities': 'generating more referrals without discounting',
        'business_trends': 'emergency call management and pricing strategies'
      },
      'Real Estate': {
        'partnership_opportunities': 'qualifying leads before showings',
        'business_trends': 'current market conditions and buyer behavior'
      },
      'HVAC': {
        'partnership_opportunities': 'building relationships with property managers',
        'business_trends': 'energy efficiency upgrades and seasonal demand'
      },
      'Construction': {
        'partnership_opportunities': 'subcontractor relationships and project coordination',
        'business_trends': 'material costs and project timeline management'
      }
    };
    return topics[industry]?.[context] || `${context.replace('_', ' ')} in the ${industry} industry`;
  };
  const getIndustrySpecificChallenge = (industry: string) => {
    const challenges: Record<string, string> = {
      'Roofing': 'staying top-of-mind with past clients during off-seasons',
      'Plumbing': 'managing emergency calls while maintaining scheduled appointments',
      'Real Estate': 'no-shows on booked appointments and unqualified leads',
      'HVAC': 'seasonal demand fluctuations and equipment supply issues',
      'Construction': 'coordinating multiple trades and managing project delays'
    };
    return challenges[industry] || `current market challenges in ${industry}`;
  };
  const getIndustrySpecificArea = (industry: string) => {
    const areas: Record<string, string> = {
      'Roofing': 'lead generation and customer retention',
      'Plumbing': 'service scheduling and emergency response',
      'Real Estate': 'client qualification and showing efficiency',
      'HVAC': 'seasonal planning and energy efficiency consulting',
      'Construction': 'project management and subcontractor coordination'
    };
    return areas[industry] || `business operations in ${industry}`;
  };
  const getIndustrySpecificStrategy = (industry: string) => {
    const strategies: Record<string, string> = {
      'Roofing': 'using automated follow-ups and seasonal maintenance reminders',
      'Plumbing': 'implementing tiered pricing for emergency vs scheduled calls',
      'Real Estate': 'pre-screening buyers with short phone consultations',
      'HVAC': 'offering maintenance contracts and energy audits',
      'Construction': 'using project management software with real-time updates'
    };
    return strategies[industry] || `implementing proven strategies for ${industry} businesses`;
  };
  const getIndustrySpecificBenefit = (industry: string) => {
    const benefits: Record<string, string> = {
      'Roofing': 'increased referrals and more consistent year-round revenue',
      'Plumbing': 'reduced emergency overtime and improved customer satisfaction',
      'Real Estate': 'higher quality leads and better closing rates',
      'HVAC': 'predictable revenue streams and improved customer loyalty',
      'Construction': 'on-time project completion and better profit margins'
    };
    return benefits[industry] || `improved efficiency and profitability`;
  };
  const getIndustrySpecificResult = (industry: string) => {
    const results: Record<string, string> = {
      'Roofing': 'increase their referral rate by 40% and maintain steady work through winter months',
      'Plumbing': 'reduce emergency call response time by 25% while increasing scheduled service revenue',
      'Real Estate': 'cut no-show rates in half and increase qualified leads by 60%',
      'HVAC': 'build a maintenance contract base worth $50k in recurring annual revenue',
      'Construction': 'complete projects 15% faster with 20% better profit margins'
    };
    return results[industry] || `achieve significant improvements in their business metrics`;
  };

  // Replace placeholders with actual values
  const replacePlaceholders = (text: string): string => {
    const phoneReplacement = formData.companyPhone || "If asked for our phone number, tell them 'The number you see on your caller ID is our main office line'";
    return text.replace(/\[Name\]/g, formData.userName || '[Name]').replace(/\[Your Name\]/g, formData.userName || '[Your Name]').replace(/\[Company\]/g, formData.companyName || '[Company]').replace(/\[Company Name\]/g, formData.companyName || '[Company Name]').replace(/\[Your Title\]/g, formData.userTitle || '[Your Title]').replace(/\[Company Phone\]/g, phoneReplacement).replace(/\[Company Website\]/g, formData.companyWebsite || '[Company Website]');
  };
  const generateScript = async () => {
    if (!isFormValid) {
      toast({
        title: "Incomplete form",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }
    setIsGenerating(true);
    try {
      // Generate main script content
      let scriptContent = '';
      if (isSafeMode(formData) || formData.scriptMode === 'standard') {
        // Use Safe Mode script builder for both Safe and Standard modes
        scriptContent = generateSafeModeScript(formData, formData.scriptMode);
      } else {
        // Sales script
        scriptContent = `💼 SALES SCRIPT
Industry: ${formData.industry}
Offer: ${formData.offer}
Goal: ${formData.callGoal}
Tone: ${formData.tone}
${formData.companyName ? `Company: ${formData.companyName}` : ''}
${formData.userName ? `Agent: ${formData.userName}` : ''}

===== OPENING =====
"Hi there, this is ${formData.userName || '[Your Name]'} from ${formData.companyName || '[Company]'}. How are you doing today?

The reason for my call is that I work specifically with ${formData.industry} businesses like yours, and I've been helping companies like yours ${formData.offer}. 

I'm not sure if this would be a fit for you, but I thought it might be worth a quick conversation. Do you have just a minute to chat?"

===== QUALIFICATION =====
"Great! Let me ask you - as someone in the ${formData.industry} space, what's your biggest challenge when it comes to ${getIndustrySpecificArea(formData.industry)}?"

[Listen to response]

"I see. And how is that impacting your business right now?"

===== VALUE PROPOSITION =====
"Well, that's exactly the kind of challenge we help ${formData.industry} businesses solve. We've been working with companies like yours to ${formData.offer}, and our clients typically see ${getIndustrySpecificBenefit(formData.industry)}.

For example, one of our clients in ${formData.industry} was facing a similar situation, and after working with us, they were able to ${getIndustrySpecificResult(formData.industry)}."

===== TRIAL CLOSE =====
"Based on what you've told me about [their challenge], it sounds like this could be valuable for you. Would you be interested in learning more about how we could help achieve similar results?"

===== OBJECTION HANDLING =====

"I'm not interested"
→ "I understand. Let me ask you this - if I could show you a way to [specific benefit related to their challenge] in the next 30 days, would that be worth a 15-minute conversation?"

"I don't have the budget"
→ "I completely understand budget concerns. Actually, that's one of the reasons our clients love working with us - we typically help them [save money/increase revenue] in ways that more than pay for our services. Would you be open to seeing how that might work for your situation?"

"I need to think about it"
→ "Absolutely. This is an important decision. Let me ask - what specifically would you need to think about? Is it the investment, the timing, or how it would work with your current setup?"

"I'm happy with my current solution"
→ "That's great to hear you have something in place! I'm curious - is your current solution helping you [specific benefit]? And are you completely satisfied with [relevant metric/result]?"

===== CLOSING =====
"Based on our conversation, it sounds like there's definitely potential for us to help [achieve specific goal]. 

I'd love to show you exactly how we've helped other ${formData.industry} companies like yours. Would you be available for a brief 15-minute demo this week? I could show you the specific strategies and results our clients are seeing."

===== CALENDAR SCHEDULING =====
"Perfect! I have some time available [specific days/times]. What works better for you - [Day 1] at [Time] or [Day 2] at [Time]?"

"Great! I'll send you a calendar invite for [confirmed time]. You'll get a lot of value from this conversation. I'm looking forward to showing you how we can help [achieve your goal]."

===== FOLLOW-UP REMINDERS =====
- Send calendar invite immediately
- Follow up with relevant case study
- Prepare customized presentation for their industry
- Research their company before the meeting
`;
      }

      // Append AI-generated intelligence if available
      if (formData.faqKeywords.length > 0) {
        scriptContent += `\n\n===== AI-GENERATED FAQ RESPONSES =====\n${formData.faqKeywords.join('\n\n')}`;
      }
      if (formData.disqualificationTriggers.length > 0) {
        scriptContent += `\n\n===== DISQUALIFICATION TRIGGERS =====\n• ${formData.disqualificationTriggers.join('\n• ')}`;
      }
      if (formData.escalationPrompts.length > 0) {
        scriptContent += `\n\n===== ESCALATION PROMPTS =====\n${formData.escalationPrompts.join('\n\n')}`;
      }

      // Add fallback logic if enabled
      if (formData.fallbackEnabled && formData.fallbackPrompt) {
        // Add fallback configuration to system message for agent runtime
        scriptContent += `\n\n===== FALLBACK SYSTEM INSTRUCTIONS =====
Fallback Detection Triggers:
- Contact speaks uninterrupted for 45+ seconds (chatty/unfocused)
- Call duration exceeds 4 minutes without clear interest signals
- AI response latency exceeds 5 seconds (technical issues)
- 2+ consecutive low-confidence AI responses

Agent Behavior Instructions:
- Execute graceful fallback sequence when triggers are met
- Log consent for callback if user agrees
- Use the fallback script lines below for consistent messaging

Runtime Configuration:
{
  "fallbackEnabled": true,
  "fallbackTriggers": {
    "maxUninterruptedContactSpeech": 45,
    "maxTotalCallDurationWithoutInterest": 240,
    "aiResponseLatency": 5000,
    "consecutiveLowConfidenceResponses": 2
  },
  "fallbackScriptLines": {
    "redirect": "Oh — I'm so sorry. A situation just came up. Would it be okay if I gave you a call back later?",
    "exit": "Thanks so much. I really appreciate your time today."
  },
  "fallbackConsentLogging": true
}`;

        // Add user-editable fallback section
        scriptContent += `\n\n===== FALLBACK RESPONSE SCRIPT =====\n${formData.fallbackPrompt}`;
      }

      // Add company details reference for AI agent
      const companyDetails = [];
      if (formData.userName) companyDetails.push(`Your Name: ${formData.userName}`);
      if (formData.userTitle) companyDetails.push(`Your Title: ${formData.userTitle}`);
      if (formData.companyName) companyDetails.push(`Company Name: ${formData.companyName}`);
      if (formData.companyPhone) {
        companyDetails.push(`Company Phone: ${formData.companyPhone}`);
      } else {
        companyDetails.push(`Company Phone: Use caller ID number as main office line`);
      }
      if (companyDetails.length > 0) {
        scriptContent += `\n\n===== COMPANY DETAILS REFERENCE =====\n${companyDetails.join('\n')}\n(Use these details if prospects ask for company information)`;
      }

      // Replace placeholders with actual user/company information
      scriptContent = replacePlaceholders(scriptContent);
      setGeneratedScript(scriptContent);
      setEditedScript(scriptContent);
      toast({
        title: "Script generated",
        description: `Your ${formData.scriptMode === 'safe' ? 'safe outreach' : formData.scriptMode === 'standard' ? 'standard mode' : 'sales'} script has been created successfully.`
      });
    } catch (error) {
      console.error('Failed to generate script:', error);
      toast({
        title: "Error",
        description: "Failed to generate script. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // PDF Export functionality
  const exportToPDF = () => {
    const pdf = new jsPDF();
    const scriptToExport = isEditing ? editedScript : generatedScript;

    // Set up the PDF
    pdf.setFontSize(16);
    pdf.text('Sales Script', 20, 20);
    pdf.setFontSize(12);
    pdf.text(`Script Name: ${formData.scriptName}`, 20, 35);
    pdf.text(`Industry: ${formData.industry}`, 20, 45);
    pdf.text(`Mode: ${formData.scriptMode === 'safe' ? 'Safe Outreach' : formData.scriptMode === 'standard' ? 'Standard Mode' : 'Sales'}`, 20, 55);

    // Add script content
    pdf.setFontSize(10);
    const splitText = pdf.splitTextToSize(scriptToExport, 170);
    pdf.text(splitText, 20, 70);

    // Save the PDF
    pdf.save(`${formData.scriptName.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_script.pdf`);
    toast({
      title: "PDF Exported",
      description: "Your script has been exported as a PDF file."
    });
  };

  // Enhanced copy functionality
  const copyScript = async (format: 'plain' | 'formatted') => {
    const scriptToCopy = isEditing ? editedScript : generatedScript;
    if (format === 'plain') {
      await navigator.clipboard.writeText(scriptToCopy);
    } else {
      // For formatted copy, we'll just use plain text but could enhance this
      await navigator.clipboard.writeText(scriptToCopy);
    }
    toast({
      title: "Script Copied",
      description: `Script copied to clipboard${format === 'formatted' ? ' with formatting' : ''}.`
    });
  };
  const handleSave = async () => {
    if (!formData.scriptName.trim()) {
      toast({
        title: "Script name required",
        description: "Please enter a name for your script.",
        variant: "destructive"
      });
      return;
    }

    // Use edited script if available, otherwise use generated script
    let scriptToSave = editedScript || generatedScript;
    if (!scriptToSave) {
      toast({
        title: "No script to save",
        description: "Please generate a script first.",
        variant: "destructive"
      });
      return;
    }

    // Apply static merge tag resolution before saving to follow vault design
    if (formData.userName || formData.companyName || formData.industry) {
      const mergeContext = {
        userName: formData.userName,
        user_name: formData.userName,
        companyName: formData.companyName,
        company_name: formData.companyName,
        userTitle: formData.userTitle,
        user_title: formData.userTitle,
        industry: formData.industry,
        businessType: formData.industry,
        business_type: formData.industry
      };

      // Apply merge tags to create resolved content for vault storage
      scriptToSave = applyStaticMergeTags(scriptToSave, mergeContext);
    }
    try {
      const scriptData = {
        name: formData.scriptName,
        mode: formData.scriptType as 'outbound' | 'inbound',
        target_industry: formData.industry,
        item_sold: formData.offer,
        content: scriptToSave,
        objections: [],
        tone: formData.tone,
        llm_provider: formData.aiProvider,
        llm_model: formData.aiModel,
        use_low_risk_mode: formData.useLowRiskMode,
        call_mode: formData.scriptMode === 'standard' ? 'standard' : formData.useLowRiskMode ? 'low_risk' : 'sales',
        safe_call_reason: formData.reasonForCall,
        enable_voicemail: formData.enableVoicemail,
        reason_for_call: formData.reasonForCall,
        target_audience: formData.targetAudience,
        routing_options: formData.routingOptions,
        faq_keywords: formData.faqKeywords.join('\n'),
        disqualification_triggers: formData.disqualificationTriggers.join('\n'),
        escalation_prompts: formData.escalationPrompts.join('\n'),
        // P.A.I.D. Framework fields
        use_paid: formData.usePaid,
        script_sections: {
          paid: {
            painStarter: formData.paidPainStarter,
            admissionPrompt: formData.paidAdmissionPrompt,
            inactionCost: formData.paidInactionCost,
            decisionCTA: formData.paidDecisionCTA
          }
        },
        // Fallback Logic fields
        fallback_enabled: formData.fallbackEnabled,
        fallback_prompt: formData.fallbackPrompt,
        // Identity fields
        user_name: formData.userName || null,
        user_title: formData.userTitle || null,
        company_name: formData.companyName || null,
        // Safe mode metadata
        safe_mode_purpose: formData.safeModePurpose || null,
        safe_mode_area: formData.area || null,
        safe_mode_channel: formData.channel || null,
        safe_mode_service: formData.service || null,
        safe_mode_custom_purpose: formData.customPurpose || null
      };
      if (editingScript) {
        await updateScript(editingScript.id, scriptData);
        toast({
          title: "Script updated",
          description: "Your script has been successfully updated."
        });
      } else {
        const newScript = await addScript(scriptData);
        toast({
          title: "Script created",
          description: "Your new script has been successfully created."
        });
        if (onScriptCreated && newScript) {
          onScriptCreated(newScript);
        }
      }
      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save script:', error);
      toast({
        title: "Error",
        description: "Failed to save script. Please try again.",
        variant: "destructive"
      });
    }
  };
  const toggleSection = (section: keyof typeof sectionsOpen) => {
    setSectionsOpen(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Mode-specific form validation
  const isFormValid = (() => {
    const basicFields = formData.scriptName && formData.scriptMode && formData.industry;
    const isSafeMode = formData.scriptMode === 'safe';
    const isStandardMode = formData.scriptMode === 'standard';
    let modeSpecificValid = true;
    if (!isSafeMode && !isStandardMode) {
      // Sales mode - offer is now optional
      modeSpecificValid = true;
    } else if (isStandardMode) {
      // Standard mode now uses Safe Mode flow with custom purpose support
      const hasCustomPurpose = formData.safeModePurpose !== 'custom' || !!formData.customPurpose;
      modeSpecificValid = !!hasCustomPurpose;
    }
    const appointmentBookingValid = !formData.appointmentBookingEnabled || formData.appointmentBookingMethod && formData.appointmentBookingUrl;
    return basicFields && modeSpecificValid && appointmentBookingValid;
  })();
  if (step === 'select-type') {
    return <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Create New Script
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <Button onClick={() => {
            setFormData(prev => ({
              ...prev,
              scriptType: 'outbound'
            }));
            setStep('form');
          }} variant="outline" className="w-full h-auto p-4 flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <PhoneOutgoing className="h-4 w-4" />
                <span className="font-medium">Outbound Script</span>
              </div>
              <p className="text-sm text-muted-foreground text-left">You (Your Agent) Calls THEM...</p>
            </Button>

            <Button onClick={() => {
            setFormData(prev => ({
              ...prev,
              scriptType: 'inbound'
            }));
            setStep('form');
          }} variant="outline" className="w-full h-auto p-4 flex flex-col items-start gap-2">
              <div className="flex items-center gap-2">
                <PhoneIncoming className="h-4 w-4" />
                <span className="font-medium">Inbound Script</span>
              </div>
              <p className="text-sm text-muted-foreground text-left">They Call You (Your Agent)...</p>
            </Button>
          </div>
        </DialogContent>
      </Dialog>;
  }
  return <TooltipProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-7xl h-[90vh] p-0">
          <DialogHeader className="px-6 py-4 border-b">
            <DialogTitle className="flex items-center gap-2">
              {formData.scriptType === 'outbound' ? <PhoneOutgoing className="h-5 w-5" /> : <PhoneIncoming className="h-5 w-5" />}
              {editingScript ? 'Edit Script' : `Create ${formData.scriptType === 'outbound' ? 'Outbound' : 'Inbound'} Script`}
            </DialogTitle>
          </DialogHeader>

          <ResizablePanelGroup direction="horizontal" className="flex-1">
            {/* Left Panel - Form */}
            <ResizablePanel defaultSize={50} minSize={40}>
              <div className="h-full overflow-y-auto p-6 space-y-6">
                
                {/* Basic Info Section */}
                <Collapsible open={sectionsOpen.basic} onOpenChange={() => toggleSection('basic')}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                    <div className="flex items-center gap-2">
                      <Settings className="h-4 w-4" />
                      <span className="font-medium">Basic Info</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.basic ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4 space-y-4">
                    <div>
                      <Label htmlFor="scriptName">Script Name</Label>
                      <Input id="scriptName" value={formData.scriptName} onChange={e => setFormData(prev => ({
                      ...prev,
                      scriptName: e.target.value
                    }))} placeholder="Enter script name..." className="mt-1" />
                    </div>

                    <div>
                      <Label htmlFor="industry">Industry/Niche</Label>
                      <Input id="industry" value={formData.industry} onChange={e => setFormData(prev => ({
                      ...prev,
                      industry: e.target.value
                    }))} placeholder="e.g., Real Estate, Dental Practices, SaaS..." className="mt-1" />
                    </div>

                    <div>
                      <Label htmlFor="tone">Tone</Label>
                      <Select value={formData.tone} onValueChange={value => setFormData(prev => ({
                      ...prev,
                      tone: value
                    }))}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {TONE_OPTIONS.map(option => <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </CollapsibleContent>
                </Collapsible>

                {/* Script Mode Selection */}
                <Collapsible open={sectionsOpen.mode} onOpenChange={() => toggleSection('mode')}>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                    <div className="flex items-center gap-2">
                      <Target className="h-4 w-4" />
                      <span className="font-medium">Script Mode Selection</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.mode ? 'rotate-180' : ''}`} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-4 space-y-4">
                    <RadioGroup value={formData.scriptMode} onValueChange={value => setFormData(prev => ({
                    ...prev,
                    scriptMode: value as 'safe' | 'sales' | 'standard'
                  }))} className="space-y-4">
                      <div className="flex items-start space-x-3 p-4 border rounded-lg">
                        <RadioGroupItem value="safe" id="safe" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="safe" className="flex items-center gap-2 cursor-pointer">
                            <Shield className="h-4 w-4 text-green-600" />
                            Safe Outreach (No Sales) 🕊️
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            Use safe, compliant outreach to warm up prospects without direct selling. Perfect for building relationships and reducing compliance risks.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 p-4 border rounded-lg">
                        <RadioGroupItem value="standard" id="standard" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="standard" className="flex items-center gap-2 cursor-pointer">
                            <Target className="h-4 w-4 text-blue-600" />
                            Standard Mode ⚡
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">This is a standard calling script developed to aid in booking call appointments for you and your team.</p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-3 p-4 border rounded-lg">
                        <RadioGroupItem value="sales" id="sales" className="mt-1" />
                        <div className="flex-1">
                          <Label htmlFor="sales" className="flex items-center gap-2 cursor-pointer">
                            <Zap className="h-4 w-4 text-orange-600" />
                            Sales Script
                          </Label>
                          <p className="text-sm text-muted-foreground mt-1">
                            Traditional sales script with value propositions, objection handling, and clear calls to action.
                          </p>
                        </div>
                      </div>
                    </RadioGroup>

                    {formData.scriptMode === 'safe' && <Alert className="mt-6">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          ⚠️ Note: This feature is designed to help reduce risk, but does not constitute legal compliance. You are solely responsible for adhering to all relevant local and federal laws, including—but not limited to—TCPA, GDPR, and telemarketing regulations.
                        </AlertDescription>
                      </Alert>}
                  </CollapsibleContent>
                </Collapsible>

                {/* User/Company Details Section */}
                {formData.scriptMode && <Collapsible open={sectionsOpen.userDetails} onOpenChange={() => toggleSection('userDetails')}>
                    <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        <span className="font-medium">Your AI Agent Details</span>
                        <Badge variant="secondary" className="text-xs">Your AI Agent's Info</Badge>
                      </div>
                      <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.userDetails ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="userName">Your Name</Label>
                          <Input id="userName" value={formData.userName} onChange={e => setFormData(prev => ({
                        ...prev,
                        userName: e.target.value
                      }))} placeholder="John Smith" className="mt-1" />
                        </div>
                        <div>
                          <Label htmlFor="userTitle">Your Title</Label>
                          <Input id="userTitle" value={formData.userTitle} onChange={e => setFormData(prev => ({
                        ...prev,
                        userTitle: e.target.value
                      }))} placeholder="Sales Representative" className="mt-1" />
                        </div>
                      </div>
                      
                      <div>
                        <Label htmlFor="companyName">Company Name</Label>
                        <Input id="companyName" value={formData.companyName} onChange={e => setFormData(prev => ({
                      ...prev,
                      companyName: e.target.value
                    }))} placeholder="ABC Solutions Inc." className="mt-1" />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="companyPhone">Company Phone</Label>
                          <Input id="companyPhone" value={formData.companyPhone} onChange={e => setFormData(prev => ({
                        ...prev,
                        companyPhone: e.target.value
                      }))} placeholder="(555) 123-4567" className="mt-1" />
                        </div>
                        <div className="hidden">
                          <Label htmlFor="companyWebsite">Company Website</Label>
                          <Input id="companyWebsite" value={formData.companyWebsite} onChange={e => setFormData(prev => ({
                        ...prev,
                        companyWebsite: e.target.value
                      }))} placeholder="www.abcsolutions.com" className="mt-1" />
                        </div>
                      </div>
                      
                      <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                          Providing these details will replace all placeholders like [Your Name], [Company], etc. with real information in your generated script.
                        </AlertDescription>
                      </Alert>
                    </CollapsibleContent>
                  </Collapsible>}

                {/* Show rest of form only after script mode is selected */}
                {formData.scriptMode && <>
                    {/* Sales Mode: Offer Field */}
                    {formData.scriptMode === 'sales' && <Collapsible open={sectionsOpen.objective} onOpenChange={() => toggleSection('objective')}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                          <div className="flex items-center gap-2">
                            <Target className="h-4 w-4" />
                            <span className="font-medium">Sales Objective</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.objective ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-4 space-y-4">
                          <div>
                            <Label htmlFor="offer">What are you selling/offering?</Label>
                            <Input id="offer" value={formData.offer} onChange={e => setFormData(prev => ({
                        ...prev,
                        offer: e.target.value
                      }))} placeholder="e.g., Marketing automation software, SEO services..." className="mt-1" />
                          </div>
                          
                          <div>
                            <Label htmlFor="callGoal">Call Goal</Label>
                            <Select value={formData.callGoal} onValueChange={value => setFormData(prev => ({
                        ...prev,
                        callGoal: value
                      }))}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="What's the goal of this call?" />
                              </SelectTrigger>
                              <SelectContent>
                                {CALL_GOALS.map(goal => <SelectItem key={goal.value} value={goal.value}>
                                    {goal.label}
                                  </SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Appointment Booking Configuration */}
                          {formData.callGoal === 'book_appointments' && <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                <Label className="font-medium">Appointment Booking Setup</Label>
                              </div>
                              
                              <div className="flex items-center space-x-2">
                                <Switch id="appointmentBookingEnabled" checked={formData.appointmentBookingEnabled} onCheckedChange={checked => setFormData(prev => ({
                          ...prev,
                          appointmentBookingEnabled: checked
                        }))} />
                                <Label htmlFor="appointmentBookingEnabled" className="text-sm">
                                  Enable appointment booking
                                </Label>
                              </div>

                              {formData.appointmentBookingEnabled && <div className="space-y-3">
                                  <div>
                                    <Label htmlFor="appointmentBookingMethod">Booking Method</Label>
                                    <Select value={formData.appointmentBookingMethod} onValueChange={value => setFormData(prev => ({
                            ...prev,
                            appointmentBookingMethod: value
                          }))}>
                                      <SelectTrigger className="mt-1">
                                        <SelectValue placeholder="Select booking method" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {BOOKING_METHODS.map(method => <SelectItem key={method.value} value={method.value}>
                                            {method.label}
                                          </SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  </div>

                                  <div>
                                    <Label htmlFor="appointmentBookingUrl">
                                      Booking URL
                                      {(contextApiKeys?.calendlyUrl || contextApiKeys?.googleCalendarUrl) && <span className="text-xs text-muted-foreground ml-1">
                                          (Auto-populated from API Settings)
                                        </span>}
                                    </Label>
                                    <Input id="appointmentBookingUrl" value={formData.appointmentBookingUrl} onChange={e => setFormData(prev => ({
                            ...prev,
                            appointmentBookingUrl: e.target.value
                          }))} placeholder="https://calendly.com/yourname or your calendar URL" className="mt-1" />
                                  </div>

                                  <div>
                                    <Label htmlFor="appointmentBookingTimezone">Timezone</Label>
                                    <Select value={formData.appointmentBookingTimezone} onValueChange={value => setFormData(prev => ({
                            ...prev,
                            appointmentBookingTimezone: value
                          }))}>
                                      <SelectTrigger className="mt-1">
                                        <SelectValue />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {TIMEZONE_OPTIONS.map(tz => <SelectItem key={tz.value} value={tz.value}>
                                            {tz.label}
                                          </SelectItem>)}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>}
                            </div>}
                        </CollapsibleContent>
                      </Collapsible>}

                    {/* Standard Mode now uses Safe Mode purpose selection with custom option */}

                    {/* Safe Mode and Standard Mode: Purpose Field */}
                    {(formData.scriptMode === 'safe' || formData.scriptMode === 'standard') && <Collapsible open={sectionsOpen.objective} onOpenChange={() => toggleSection('objective')}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                          <div className="flex items-center gap-2">
                            <Shield className="h-4 w-4" />
                            <span className="font-medium">Outreach Purpose</span>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.objective ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-4 space-y-4">
                          <div>
                            <Label>Purpose Type</Label>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                              {[{
                          value: 'referral',
                          label: 'Referral/Leads Inquiry'
                        }, {
                          value: 'interview',
                          label: 'Interview/Feature'
                        }, {
                          value: 'directory',
                          label: 'Directory Listing'
                        }, {
                          value: 'googlemaps',
                          label: 'Google Maps Verification'
                        }, {
                          value: 'custom',
                          label: 'Custom'
                        }].map(p => <label key={p.value} className="flex items-center gap-2 border rounded-xl p-2 cursor-pointer hover:bg-muted/50">
                                  <input type="radio" name="safeModePurpose" value={p.value} checked={formData.safeModePurpose === p.value} onChange={e => setFormData(prev => ({
                            ...prev,
                            safeModePurpose: e.target.value as any
                          }))} />
                                  <span className="text-sm">{p.label}</span>
                                </label>)}
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label>Area/Location</Label>
                              <Input value={formData.area || ''} onChange={e => setFormData(prev => ({
                          ...prev,
                          area: e.target.value
                        }))} placeholder="e.g., your area, downtown, etc." className="mt-1" />
                              {!formData.area && <div className="text-xs text-muted-foreground mt-1">Using default: "your area"</div>}
                            </div>

                            {formData.safeModePurpose === 'interview' && <div>
                                <Label>Channel/Medium</Label>
                                <Input value={formData.channel || ''} onChange={e => setFormData(prev => ({
                          ...prev,
                          channel: e.target.value
                        }))} placeholder="e.g., podcast, blog post, etc." className="mt-1" />
                                {!formData.channel && <div className="text-xs text-muted-foreground mt-1">Using default: "a quick recorded interview"</div>}
                              </div>}

                            {formData.safeModePurpose === 'referral' && <div>
                                <Label>Service/Program</Label>
                                <Input value={formData.service || ''} onChange={e => setFormData(prev => ({
                          ...prev,
                          service: e.target.value
                        }))} placeholder="e.g., referral program, lead service" className="mt-1" />
                                {!formData.service && <div className="text-xs text-muted-foreground mt-1">Using default: "referral opportunities"</div>}
                              </div>}

                            {formData.safeModePurpose === 'custom' && <div>
                                <Label>Custom Purpose <span className="text-red-500">*</span></Label>
                                <Input value={formData.customPurpose || ''} onChange={e => setFormData(prev => ({
                          ...prev,
                          customPurpose: e.target.value
                        }))} placeholder="Enter your custom purpose..." className="mt-1" required />
                                <div className="text-xs text-muted-foreground mt-1">Keep it under 20 words for best results</div>
                              </div>}
                          </div>

                        </CollapsibleContent>
                      </Collapsible>}

                    {/* AI Provider Selection */}
                    <Collapsible open={sectionsOpen.ai} onOpenChange={() => toggleSection('ai')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                        <div className="flex items-center gap-2">
                          <Brain className="h-4 w-4" />
                          <span className="font-medium">AI Provider & Model</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.ai ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-4 space-y-4">
                        <div>
                          <Label htmlFor="aiProvider">AI Provider</Label>
                          <Select value={formData.aiProvider} onValueChange={value => {
                        setFormData(prev => ({
                          ...prev,
                          aiProvider: value,
                          aiModel: AI_MODELS[value as keyof typeof AI_MODELS]?.[0]?.value || ''
                        }));
                      }}>
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select AI Provider" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableProviders.includes('openai') && <SelectItem value="openai">OpenAI</SelectItem>}
                              {availableProviders.includes('grok') && <SelectItem value="grok">Grok (X.AI)</SelectItem>}
                            </SelectContent>
                          </Select>
                          {availableProviders.length === 0 && <Alert className="mt-2">
                              <AlertTriangle className="h-4 w-4" />
                              <AlertDescription>
                                No AI providers configured. Please add your API keys in API Settings.
                              </AlertDescription>
                            </Alert>}
                        </div>

                        {formData.aiProvider && <div>
                            <Label htmlFor="aiModel">AI Model</Label>
                            <Select value={formData.aiModel} onValueChange={value => setFormData(prev => ({
                        ...prev,
                        aiModel: value
                      }))}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Select AI Model" />
                              </SelectTrigger>
                              <SelectContent>
                                {AI_MODELS[formData.aiProvider as keyof typeof AI_MODELS]?.map(model => <SelectItem key={model.value} value={model.value}>
                                    {model.label}
                                  </SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* AI Intelligence Section */}
                    <Collapsible open={sectionsOpen.intelligence} onOpenChange={() => toggleSection('intelligence')}>
                      <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                        <div className="flex items-center gap-2">
                          <Brain className="h-4 w-4" />
                          <span className="font-medium">AI Intelligence</span>
                          <Badge variant="outline" className="text-xs">Optional</Badge>
                        </div>
                        <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.intelligence ? 'rotate-180' : ''}`} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="mt-4 space-y-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">Generate AI-Powered Content</h4>
                            <p className="text-sm text-muted-foreground">
                              {formData.scriptMode === 'safe' ? 'Generate FAQ responses for relationship building.' : 'Generate FAQs, objections, disqualifiers, and escalation prompts.'}
                            </p>
                          </div>
                          <Button onClick={generateIntelligence} disabled={isGeneratingIntelligence || !formData.industry || !formData.scriptMode} variant="outline">
                            {isGeneratingIntelligence ? <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Generating...
                              </> : <>
                                <Brain className="h-4 w-4 mr-2" />
                                Generate
                              </>}
                          </Button>
                        </div>

                        {/* Show generated content */}
                        {(formData.faqKeywords.length > 0 || formData.disqualificationTriggers.length > 0 || formData.escalationPrompts.length > 0) && <div className="space-y-4 p-4 bg-muted/20 rounded-lg">
                            <h5 className="font-medium text-sm">Generated Content Preview:</h5>
                            
                            {formData.faqKeywords.length > 0 && <div>
                                <Label className="text-sm font-medium">FAQ Responses ({formData.faqKeywords.length})</Label>
                                <div className="mt-2 text-xs bg-muted/50 p-2 rounded max-h-24 overflow-y-auto">
                                  {formData.faqKeywords.slice(0, 2).map((faq, index) => <div key={index} className="mb-2 last:mb-0">
                                      {faq.split('\n')[0]}...
                                    </div>)}
                                  {formData.faqKeywords.length > 2 && <div className="text-muted-foreground">+{formData.faqKeywords.length - 2} more...</div>}
                                </div>
                              </div>}

                            {formData.disqualificationTriggers.length > 0 && <div>
                                <Label className="text-sm font-medium">Disqualification Triggers ({formData.disqualificationTriggers.length})</Label>
                                <div className="mt-2 text-xs bg-muted/50 p-2 rounded max-h-24 overflow-y-auto">
                                  {formData.disqualificationTriggers.join(', ')}
                                </div>
                              </div>}

                            {formData.escalationPrompts.length > 0 && <div>
                                <Label className="text-sm font-medium">Escalation Prompts ({formData.escalationPrompts.length})</Label>
                                <div className="mt-2 text-xs bg-muted/50 p-2 rounded max-h-24 overflow-y-auto">
                                  {formData.escalationPrompts.join(', ')}
                                </div>
                              </div>}
                          </div>}
                      </CollapsibleContent>
                    </Collapsible>

                    {/* P.A.I.D. Framework Section - Only show for outbound sales scripts */}
                    {formData.scriptType === 'outbound' && formData.scriptMode === 'sales' && <Collapsible open={sectionsOpen.paid} onOpenChange={() => toggleSection('paid')}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                          <div className="flex items-center gap-2">
                            <Target className="h-4 w-4" />
                            <span className="font-medium">P.A.I.D. Framework</span>
                            <Badge variant="outline" className="text-xs">Sales</Badge>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.paid ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-4 space-y-4">
                          <div className="flex items-center gap-2 mb-4">
                            <Switch checked={formData.usePaid} onCheckedChange={checked => setFormData(prev => ({
                        ...prev,
                        usePaid: checked
                      }))} />
                            <Label className="font-medium">Enable P.A.I.D. Framework</Label>
                          </div>
                          
                          {formData.usePaid && <div className="space-y-4 pl-6 border-l-2 border-primary/20">
                              <div>
                                <Label htmlFor="paidPainStarter">Pain Starter</Label>
                                <Textarea id="paidPainStarter" value={formData.paidPainStarter} onChange={e => setFormData(prev => ({
                          ...prev,
                          paidPainStarter: e.target.value
                        }))} className="mt-1" placeholder="How do you currently handle [pain point]? What's that process like for you?" rows={3} />
                              </div>

                              <div>
                                <Label htmlFor="paidAdmissionPrompt">Admission Prompt</Label>
                                <Textarea id="paidAdmissionPrompt" value={formData.paidAdmissionPrompt} onChange={e => setFormData(prev => ({
                          ...prev,
                          paidAdmissionPrompt: e.target.value
                        }))} className="mt-1" placeholder="On a scale of 1-10, how big of a problem is this for you?" rows={3} />
                              </div>

                              <div>
                                <Label htmlFor="paidInactionCost">Inaction Cost</Label>
                                <Textarea id="paidInactionCost" value={formData.paidInactionCost} onChange={e => setFormData(prev => ({
                          ...prev,
                          paidInactionCost: e.target.value
                        }))} className="mt-1" placeholder="What happens if this problem isn't solved in the next 6 months?" rows={3} />
                              </div>

                              <div>
                                <Label htmlFor="paidDecisionCTA">Decision Call-to-Action</Label>
                                <Textarea id="paidDecisionCTA" value={formData.paidDecisionCTA} onChange={e => setFormData(prev => ({
                          ...prev,
                          paidDecisionCTA: e.target.value
                        }))} className="mt-1" placeholder="Based on what you've shared, would you like to explore how we can help?" rows={3} />
                              </div>
                            </div>}
                        </CollapsibleContent>
                      </Collapsible>}

                    {/* Fallback Logic Section - Only show for outbound scripts */}
                    {formData.scriptType === 'outbound' && <Collapsible open={sectionsOpen.fallback} onOpenChange={() => toggleSection('fallback')}>
                        <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="font-medium">Fallback Logic</span>
                            <Badge variant="outline" className="text-xs">Outbound</Badge>
                          </div>
                          <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen.fallback ? 'rotate-180' : ''}`} />
                        </CollapsibleTrigger>
                        <CollapsibleContent className="mt-4 space-y-4">
                          <div className="flex items-center gap-2 mb-4">
                            <Switch checked={formData.fallbackEnabled} onCheckedChange={checked => setFormData(prev => ({
                        ...prev,
                        fallbackEnabled: checked
                      }))} />
                            <Label className="font-medium">Enable Fallback Logic for Long or Unproductive Calls</Label>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p>Injects graceful fallback behavior into your script and enables runtime detection for long-winded, low-interest, or laggy calls. Helps your AI agent exit politely and log consent for future follow-ups.</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          
                          {formData.fallbackEnabled && <div className="pl-6 border-l-2 border-orange-200">
                              <div>
                                <Label htmlFor="fallbackPrompt">Fallback Instructions</Label>
                                <Textarea id="fallbackPrompt" value={formData.fallbackPrompt} onChange={e => setFormData(prev => ({
                          ...prev,
                          fallbackPrompt: e.target.value
                        }))} className="mt-1" placeholder="Define when and how the AI should gracefully end unproductive calls" rows={6} />
                                <p className="text-xs text-muted-foreground mt-2">
                                  Define when and how the AI should gracefully end unproductive calls
                                </p>
                              </div>
                            </div>}
                        </CollapsibleContent>
                      </Collapsible>}

                    {/* Generate Script Button */}
                    <div className="pt-4">
                      <Button onClick={generateScript} disabled={isGenerating || !isFormValid} className="w-full" size="lg">
                        {isGenerating ? <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Generating Script...
                          </> : <>
                            <Zap className="h-4 w-4 mr-2" />
                            Generate Script
                          </>}
                      </Button>
                    </div>
                  </>}
              </div>
            </ResizablePanel>

            <ResizableHandle />

            {/* Right Panel - Preview */}
            <ResizablePanel defaultSize={50} minSize={40}>
              <div className="h-full flex flex-col">
                <div className="p-4 border-b bg-muted/20">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      <span className="font-medium">Generated Script</span>
                      {formData.scriptMode === 'safe' && <Badge variant="outline" className="text-green-600 border-green-200">
                          🕊️ Safe Mode
                        </Badge>}
                    </div>
                    {generatedScript && <div className="flex items-center gap-2">
                        <Button onClick={() => copyScript('plain')} variant="outline" size="sm">
                          <Copy className="h-4 w-4 mr-1" />
                          Copy
                        </Button>
                        <Button onClick={exportToPDF} variant="outline" size="sm">
                          <Download className="h-4 w-4 mr-1" />
                          PDF
                        </Button>
                      </div>}
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {generatedScript ? <div className="space-y-4">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Edit3 className="h-4 w-4" />
                        <span>Script is editable - make changes directly below</span>
                      </div>
                      <Textarea value={editedScript || extractSpokenContent(generatedScript)} onChange={e => setEditedScript(e.target.value)} className="min-h-[500px] font-mono text-sm resize-none" placeholder="Your script content will appear here..." />
                    </div> : <div className="flex items-center justify-center h-full text-center">
                      <div className="space-y-2">
                        <FileText className="h-12 w-12 mx-auto text-muted-foreground/50" />
                        <h3 className="font-medium text-muted-foreground">No Script Generated</h3>
                        <p className="text-sm text-muted-foreground">
                          Fill out the form and click "Generate Script" to see your script preview here.
                        </p>
                      </div>
                    </div>}
                </div>

                {generatedScript && <div className="p-4 border-t bg-muted/20">
                    <div className="flex gap-2">
                      <Button onClick={() => {
                    // Update generated script with edited version before saving
                    if (editedScript && editedScript !== generatedScript) {
                      setGeneratedScript(editedScript);
                    }
                    handleSave();
                  }} className="flex-1">
                        {editingScript ? 'Update Script' : 'Save Script'}
                      </Button>
                      <Button onClick={() => onOpenChange(false)} variant="outline">
                        Cancel
                      </Button>
                    </div>
                  </div>}
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        </DialogContent>
      </Dialog>
    </TooltipProvider>;
}
export default ScriptCreator;